import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    Spinner,
    NoticeBox,
    NoticeBoxColors,
    NoticeBoxVariants,
    VsExclamationIcon,
} from '@octane/spark'
import { getCollaterals } from 'api/application'
import {
    CollateralResponse,
    PowersportsAssetResponse,
} from 'api/application/getCollaterals'
import Card from 'components/Card'
import { ApprovalOemPortalJumpCta } from 'components/OemPortalJumpCta'
import useClientApplication from 'hooks/useClientApplication'
import ApplicationPortal from 'state/enums/ApplicationPortal'
import ApplicationStatus from 'state/enums/ApplicationStatus'
import getPortal from 'utils/getPortal'

import ApprovalDisclosure from './ApprovalDisclosure'
import { MainContainer, LoaderWrapper } from './DecisionResult.styled'
import DecisionResultApproved from './DecisionResultApproved'
import DecisionResultCollateralCard from './DecisionResultCollateralCard'
import DecisionResultCustomerDetailCard from './DecisionResultCustomerDetailCard'
import DecisionResultDeclined from './DecisionResultDeclined'
import DecisionResultSubmitted from './DecisionResultSubmitted'

export default function DecisionResult(): JSX.Element {
    const { applicationId } = useParams()

    const { clientApplication, loading, error } =
        useClientApplication(applicationId)

    const [collaterals, setCollaterals] = useState<
        CollateralResponse<PowersportsAssetResponse>[]
    >([])

    const handleCollaterals = async () => {
        if (clientApplication?.uuid) {
            setCollaterals(await getCollaterals(clientApplication.uuid))
        }
    }

    useEffect(() => {
        handleCollaterals()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientApplication])

    if (error) {
        return (
            <MainContainer>
                <NoticeBox
                    title=""
                    icon={VsExclamationIcon}
                    color={NoticeBoxColors.WARNING}
                    variant={NoticeBoxVariants.BOX_HIGHLIGHTED}
                >
                    An error occurred when loading the decision. Refresh the
                    page to try again or contact support.
                </NoticeBox>
            </MainContainer>
        )
    }

    if (loading || !clientApplication || !collaterals) {
        return (
            <LoaderWrapper data-testid="DecisionResult-spinner">
                <Spinner />
            </LoaderWrapper>
        )
    }

    const renderContent = () => {
        switch (clientApplication?.status) {
            case ApplicationStatus.APPROVED:
                return (
                    <DecisionResultApproved
                        clientApplication={clientApplication}
                        vehicleType={collaterals[0]?.asset?.vehicle_type}
                        vehicleCondition={collaterals[0]?.asset?.condition}
                    />
                )

            case ApplicationStatus.DECLINE:
                return (
                    <DecisionResultDeclined
                        clientApplication={clientApplication}
                    />
                )

            default:
                return (
                    <DecisionResultSubmitted
                        clientApplication={clientApplication}
                    />
                )
        }
    }

    return (
        <MainContainer>
            <Card style={{ padding: '20px' }}>{renderContent()}</Card>
            {getPortal() === ApplicationPortal.OCTANE &&
                clientApplication.is_oem_portal_eligible && (
                    <ApprovalOemPortalJumpCta
                        applicationUuid={clientApplication.uuid}
                    />
                )}
            {getPortal() === ApplicationPortal.OCTANE &&
                clientApplication.is_forest_river_oem_portal_eligible && (
                    <ApprovalOemPortalJumpCta
                        applicationUuid={clientApplication.uuid}
                        brand="forestRiver"
                    />
                )}
            <DecisionResultCustomerDetailCard
                clientApplication={clientApplication}
            />
            <DecisionResultCollateralCard
                applicationId={clientApplication.id}
                collaterals={collaterals}
            />
            <ApprovalDisclosure applicationId={clientApplication.id} />
        </MainContainer>
    )
}
